/**
 * Types for the Settings system
 */

/**
 * Validation rules for a setting
 */
export interface SettingValidationRules {
    min?: number;
    max?: number;
    regex?: string;
    allowedValues?: string[];
    required?: boolean;
}

/**
 * Setting definition from master database
 */
export interface SettingDefinition {
    settingKey: string;
    displayName: string;
    description?: string;
    category: string;
    dataType: 'String' | 'Int' | 'Bool' | 'Decimal' | 'DateTime' | 'Json';
    defaultValue?: string;
    allowSystemLevel: boolean;
    allowProfileLevel: boolean;
    validationRules?: SettingValidationRules;
    displayOrder: number;
    isActive: boolean;
}

/**
 * Combined setting with resolved value for UI display
 */
export interface SettingWithValue {
    definition: SettingDefinition;
    currentValue?: string;
    effectiveSource: 'Profile' | 'System' | 'Default';
    systemValue?: string;
    profileValue?: string;
}

/**
 * Setting value with metadata
 */
export interface SettingValue {
    settingKey: string;
    value?: string;
    lastUpdate?: string;
    updatedBy?: string;
}

/**
 * Validation result for a setting value
 */
export interface SettingValidationResult {
    isValid: boolean;
    errors: string[];
}

/**
 * Category summary
 */
export interface SettingCategory {
    category: string;
    displayName?: string;
    settingCount: number;
}

/**
 * Request to create a setting definition
 */
export interface CreateSettingDefinitionRequest {
    settingKey: string;
    displayName: string;
    description?: string;
    category: string;
    dataType: string;
    defaultValue?: string;
    allowSystemLevel?: boolean;
    allowProfileLevel?: boolean;
    validationRules?: SettingValidationRules;
    displayOrder?: number;
}

/**
 * Request to update a setting definition
 */
export interface UpdateSettingDefinitionRequest {
    displayName: string;
    description?: string;
    category: string;
    dataType: string;
    defaultValue?: string;
    allowSystemLevel?: boolean;
    allowProfileLevel?: boolean;
    validationRules?: SettingValidationRules;
    displayOrder?: number;
}

/**
 * Request to set a setting value
 */
export interface SetSettingValueRequest {
    value?: string;
}

/**
 * Request to set multiple settings at once
 */
export interface BulkSetSettingsRequest {
    settings: Record<string, string | undefined>;
}

/**
 * Grouped settings by category for UI
 */
export interface GroupedSettings {
    category: string;
    displayName: string;
    settings: SettingWithValue[];
}

/**
 * Category display names (Norwegian)
 */
export const CATEGORY_DISPLAY_NAMES: Record<string, string> = {
    general: 'Generelle innstillinger',
    picking: 'Plukking',
    receipt: 'Varemottak',
    putaway: 'Vareplassering',
    'warehouse-transfer': 'Lagerflytt',
    replenishment: 'Påfylling',
    printing: 'Utskrift',
    email: 'E-post',
    integration: 'Integrasjoner',
    storage: 'Lagring',
    logging: 'Logging',
    scanning: 'Skanning'
};

/**
 * Data type display names (Norwegian)
 */
export const DATA_TYPE_DISPLAY_NAMES: Record<string, string> = {
    String: 'Tekst',
    Int: 'Heltall',
    Bool: 'Ja/Nei',
    Decimal: 'Desimaltall',
    DateTime: 'Dato/tid',
    Json: 'JSON'
};
